<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import type { Survey } from '@/types/types'
import type { I18nStrings } from './interfaces'
import { isValidUrl } from './utils'

const { t } = useI18n()

const props = defineProps<{
  modelValue: string
  valueType?: 'link' | 'pageId'
  customValueModel?: 'survey'
  i18nStrings: I18nStrings
  isEditing: boolean
  surveys?: Survey[]
  isLoadingSurveys?: boolean
  loadSurveysError?: boolean
  selectOpen?: boolean
  requiredStoreIds?: number[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'save'): void
  (e: 'back'): void
  (e: 'update:selectOpen', value: boolean): void
}>()

const customValue = useVModel(props, 'modelValue', emit)
const selectOpenModel = useVModel(props, 'selectOpen', emit)

const actionButtonText = computed(() => {
  return props.isEditing ? props.i18nStrings.customValue?.update : props.i18nStrings.customValue?.create
})

const isUrl = computed(() => isValidUrl(customValue.value))

const isSurveyPageId = computed(() => props.valueType === 'pageId' && props.customValueModel === 'survey')

const surveys = computed(() => props.surveys ?? [])
const loadSurveysError = computed(() => props.loadSurveysError === true)
const isLoadingSurveys = computed(() => props.isLoadingSurveys === true)

const surveySelectionError = (surveyId: number): 'surveyNotFound' | 'requiredStoresNotCovered' | null => {
  if (!surveyId) return null

  const survey = surveys.value.find((s) => s.id === surveyId)
  if (!survey) return 'surveyNotFound'

  if (!props.requiredStoreIds?.length) return null

  const surveyStoreIdSet = new Set(survey.stores?.map((s) => s.id) ?? [])
  const hasAllRequiredStores = props.requiredStoreIds.every((id) => surveyStoreIdSet.has(id))
  return hasAllRequiredStores ? null : 'requiredStoresNotCovered'
}

const currentSurveyError = computed(() => surveySelectionError(Number(customValue.value)))

const canSave = computed(() => {
  if (props.valueType === 'link') {
    return customValue.value.length > 0 && isUrl.value
  }

  if (isSurveyPageId.value) {
    return (
      customValue.value.length > 0 &&
      !isLoadingSurveys.value &&
      !loadSurveysError.value &&
      currentSurveyError.value === null
    )
  }
  return false
})
</script>

<template>
  <li
    class="cursor-pointer px-4 py-3 border-b flex flex-row items-center justify-between border-border hover:bg-muted/50 gap-2"
    @click="emit('back')"
  >
    <BaseIcon name="HeroChevronLeftIconSolid" class="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
    <p class="text-sm text-muted-foreground flex-1">
      {{ t('global.words.back') }}
    </p>
  </li>

  <div v-if="valueType === 'link'" class="flex flex-col p-3">
    <Input id="customValue" v-model="customValue" :placeholder="i18nStrings.customValue?.placeholder" />

    <div v-if="!isUrl && customValue.length > 0" class="mt-1">
      <p class="text-xs text-red-500">
        {{ t('global.errors.unvalidUrl') }}
      </p>
    </div>
  </div>

  <div v-else-if="isSurveyPageId" class="flex flex-col p-3">
    <template v-if="isLoadingSurveys">
      <div class="gap-2 flex flex-col">
        <p class="text-sm text-muted-foreground">{{ t('global.words.loading') }}…</p>
      </div>
    </template>

    <template v-else-if="loadSurveysError">
      <div class="gap-2 flex flex-col">
        <p class="text-sm text-red-600">
          {{ t('ui.textareaWithVars.survey.loadFailed') }}
        </p>
      </div>
    </template>

    <template v-else>
      <Select v-model="customValue" v-model:open="selectOpenModel">
        <SelectTrigger class="h-10 w-full">
          <span class="text-sm text-muted-foreground truncate">
            {{
              customValue
                ? surveys.find((s) => s.id === Number(customValue))?.questionText
                : t('ui.textareaWithVars.survey.selectPlaceholder')
            }}
          </span>
        </SelectTrigger>
        <SelectContent class="max-h-72" :disable-portal="true">
          <template v-for="survey in surveys" :key="survey.id">
            <AppTooltip
              v-if="surveySelectionError(survey.id) !== null"
              :text="t(`ui.textareaWithVars.survey.requiredStoresNotCovered`)"
            >
              <SurveySelectOption :survey="survey" dimmed />
            </AppTooltip>

            <SelectItem v-else :value="String(survey.id)">
              <SurveySelectOption :survey="survey" />
            </SelectItem>
          </template>
        </SelectContent>
      </Select>

      <p v-if="currentSurveyError" class="mt-2 text-xs text-red-500">
        {{ t(`ui.textareaWithVars.survey.${currentSurveyError}`) }}
      </p>
    </template>
  </div>

  <div class="flex flex-row p-3 pt-0 justify-end">
    <Button variant="default-primary" :disabled="!canSave" @click="emit('save')">
      {{ actionButtonText }}
    </Button>
  </div>
</template>
